import clsx from 'clsx';
import { ReactElement } from 'react';

import styles from './Table.module.scss';

export interface TableProps {
  /** Extra class forwarded to the `<table>`. */
  className?: string;
  /** Apply the hover treatment to body rows. Defaults to `true`. */
  withBodyRowHover?: boolean;
  /** Content rendered inside `<thead>` (typically `<TableHeader>` rows). */
  TableHeaderContent: ReactElement | ReactElement[];
  /** Content rendered inside `<tbody>` (typically `<TableCell>` rows). */
  TableBodyContent: ReactElement | ReactElement[];
  /** When true, the `<thead>` stays pinned to the top while the body scrolls. Defaults to `false`. */
  isHeaderSticky?: boolean;
}

/**
 * Low-level styled `<table>` wrapper. Provides design-system table
 * styling (hover, optional sticky header) and accepts pre-built header
 * and body content as React nodes — pair with `TableHeader` and
 * `TableCell` (and `WithNAFallback` for empty values).
 *
 * For data-driven tables prefer `TableV2` (TanStack-based, with column
 * types and pinning) or `SmartTable` (legacy, deprecated).
 */
export const Table = ({
  withBodyRowHover = true,
  TableHeaderContent,
  TableBodyContent,
  className,
  isHeaderSticky = false,
}: TableProps) => {
  return (
    <table
      className={clsx(
        styles.table,
        withBodyRowHover && styles.withBodyRowHover,
        isHeaderSticky && styles.isHeaderSticky,
        className
      )}
    >
      <thead>{TableHeaderContent}</thead>
      <tbody>{TableBodyContent}</tbody>
    </table>
  );
};
